import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { COUNTRIES } from 'constants';

const EsporaiPhoneInput = ({
  value,
  onChange,
  className,
  disabled,
  placeholder,
  label,
  error,
  prefix,
  onChangePrefix,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownHeight, setDropdownHeight] = useState(200);
  const [dropdownMinWidth, setDropdownMinWidth] = useState(0);
  const wrapperRef = useRef(null);
  const dropdownContainerRef = useRef(null);

  const filteredCountries = COUNTRIES.filter(c => {
    const term = searchTerm.toLowerCase();
    return (
      c.name.toLowerCase().includes(term) ||
      c.code.toLowerCase().includes(term) ||
      c.prefix.includes(term)
    );
  });

  const selectedCountry = COUNTRIES.find(c => c.prefix === prefix) || COUNTRIES[0];

  const baseClasses = 'pl-4 py-3 bg-white border focus:outline-none transition-colors duration-200';
  const stateClasses = {
    normal: 'border-gray-light text-gray-dark placeholder-gray focus:border-gray',
    error: 'border-error text-gray-dark placeholder-gray focus:border-error',
    disabled: 'opacity-60 bg-gray-light cursor-not-allowed',
  };
  const getStateClass = () => {
    if (disabled) return stateClasses.disabled;
    if (error) return stateClasses.error;
    return stateClasses.normal;
  };

  const handleChange = e => {
    const numericValue = e.target.value.replace(/\D/g, '');
    onChange(numericValue);
  };

  const toggleDropdown = () => {
    if (!disabled) setIsOpen(prev => !prev);
  };

  const selectCountry = country => {
    onChangePrefix(country.prefix);
    setIsOpen(false);
    setSearchTerm('');
  };

  const getScrollParent = useCallback(element => {
    let parent = element.parentElement;
    while (parent) {
      const overflowY = window.getComputedStyle(parent).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return parent;
      }
      parent = parent.parentElement;
    }
    return document.body;
  }, []);

  const updateDropdownDimensions = useCallback(() => {
    if (!dropdownContainerRef.current) return;
    const rect = dropdownContainerRef.current.getBoundingClientRect();
    const scrollParent = getScrollParent(dropdownContainerRef.current);
    const parentRect = scrollParent.getBoundingClientRect();
    const availableSpace = parentRect.bottom - rect.bottom - 16;
    setDropdownHeight(availableSpace > 100 ? availableSpace : 100);
    setDropdownMinWidth(rect.width);
  }, [getScrollParent]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      updateDropdownDimensions();
    }
  }, [isOpen, updateDropdownDimensions]);

  return (
    <div className="w-full relative" ref={wrapperRef}>
      {label && <label className="block text-gray-dark mb-2 font-medium">{label}</label>}
      <div className="flex">
        <div className="phone-dropdown relative" ref={dropdownContainerRef}>
          <button
            type="button"
            className={`flex items-center justify-between min-w-[90px] px-3 ${baseClasses} ${getStateClass()} rounded-l-xl border-r-0 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
            onClick={toggleDropdown}
            disabled={disabled}
          >
            <div className="flex items-center overflow-hidden">
              <span className="text-lg flex-shrink-0">{selectedCountry.flag}</span>
              <span className="mx-1.5 text-sm whitespace-nowrap">{selectedCountry.prefix}</span>
            </div>
            <ChevronDown
              size={16}
              className={`ml-1 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </button>
          {isOpen && (
            <div
              className="absolute z-10 mt-1 bg-white border border-gray-light rounded-lg shadow-lg overflow-y-auto"
              style={{ minWidth: `${dropdownMinWidth}px`, maxHeight: `${dropdownHeight}px` }}
            >
              <div className="px-3 py-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Buscar país"
                  className="w-full px-3 py-2 border-b border-gray-light focus:outline-none"
                />
              </div>
              {filteredCountries.map(country => (
                <button
                  key={country.code}
                  type="button"
                  className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-50"
                  onClick={() => selectCountry(country)}
                >
                  <span className="text-lg mr-2 flex-shrink-0">{country.flag}</span>
                  <span className="flex-1 truncate">{country.name}</span>
                  <span className="ml-2 text-gray-500 flex-shrink-0">{country.prefix}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          type="tel"
          value={value}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          className={`${baseClasses} ${getStateClass()} rounded-r-xl flex-1 ${className}`}
          {...props}
        />
      </div>
      <div className="h-5 mt-2">
        {error && typeof error === 'string' && <p className="text-sm text-error">{error}</p>}
      </div>
    </div>
  );
};

export default EsporaiPhoneInput;
